/**
 * @voltxampere/engine — OpAmp single-pole macro DC stamp
 *
 * Sprint 7 ships a soft-clipping VCVS macro model:
 *
 *   V_target = Vsat · tanh(A0 · V_diff / Vsat)
 *
 * where V_diff = V_in_pos − V_in_neg.  The tanh gives a smooth
 * saturation knee (derivative-continuous at the rails), which is
 * what Newton-Raphson needs to converge on large-signal inputs
 * without the stability issues of piecewise-linear clamping.  For
 * small V_diff the model reduces to V_out ≈ A0 · V_diff — the
 * textbook linear open-loop amplifier.
 *
 * Implementation via a low-impedance Norton at the output:
 *
 *   output node sees:  V_out / Rout current drawn to virtual
 *   ground  ⇐⇒  Y[out][out] += 1/Rout
 *   output also gets a `V_target / Rout` current source into the
 *   node  ⇐⇒  rhs[out] += V_target / Rout
 *
 * The tanh linearisation adds off-diagonal admittance from the
 * input pins:
 *
 *   dV_target/dV_pos =  A0 · sech²(arg)  ≡ g_in
 *   dV_target/dV_neg = −g_in
 *
 *   Y[out][in_pos] −= g_in / Rout
 *   Y[out][in_neg] += g_in / Rout
 *
 * Rout is fixed at 1 Ω internally. That is small enough that a
 * feedback loop closes cleanly without being pathological for the
 * sparse LU pivot. External loads pull the rail slightly toward
 * their own impedance weighted by Rout — a 10 kΩ feedback resistor
 * sees 10000× stronger than the OpAmp's own Rout, so the rail
 * "wins" and the OpAmp produces the target voltage to ≈ 0.01 %.
 *
 * Inputs are modelled as ideal (no input bias current) — no
 * admittance contributions at the in_pos / in_neg rows.
 *
 * Transient: Sprint 7 ships DC only.  The single pole at
 * `poleFrequency` will land with a companion capacitor in Sprint 8
 * when the AC analysis needs Bode shaping.
 *
 * ── Sprint 54.2a — Comparator extensions (LM393 / LM339) ──────────
 *
 * Two opt-in params unlock comparator-style behaviour while leaving
 * the Sprint 7 op-amp path bit-identical for all callers that don't
 * set them (every Family 1-3 op-amp, every legacy circuit).
 *
 * 1. `outputMode: "open-collector"` — LM393/LM339 open-collector
 *    output stage. Asymmetric drive:
 *      - When tanh argument < 0 (output should sink LOW): Norton
 *        drives to `outputLowVoltage` (typ 0.4 V at 16 mA sink).
 *      - When tanh argument >= 0 (output should release HIGH): the
 *        stamp emits a near-zero output conductance only, letting
 *        the external pull-up + load network determine V_out.
 *
 * 2. `hysteresis: true` — Schmitt-trigger latching. State-locked
 *    digital output derived from the previous Newton iterate
 *    (`xPrev[out]`), with separate `vThresholdPlus` /
 *    `vThresholdMinus` thresholds on V_diff. Pattern mirrors the
 *    Sprint 41 ADR-0064 digital-gate Schmitt branch. The opt-in
 *    bypasses tanh linearisation: output is forced to high or low
 *    via a constant Norton with no input coupling (preserves
 *    Newton stability across the discrete state flip).
 *
 * Mutually-exclusive priority: when both flags are set, Schmitt
 * wins (the latch is the dominant semantic). Open-collector still
 * applies to the LOW rail selection in that case.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
/** Internal output resistance. 1 Ω is low enough to dominate any
 *  reasonable feedback resistor network (~1000× stiffer than a
 *  10 kΩ feedback) while staying well-conditioned in double
 *  precision. */
const ROUT = 1;
/** Open-collector HIGH-state output conductance to ground. Near
 *  zero so the external pull-up dominates; non-zero so the LU
 *  pivot for the output row stays well-conditioned. */
const OPEN_COLLECTOR_HIGH_G = 1e-12;
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
function readComparatorParams(component) {
    const params = component.params;
    const outputModeRaw = params.outputMode;
    const outputMode = outputModeRaw === "open-collector" ? "open-collector" : "standard";
    const hysteresisRaw = params.hysteresis;
    const hysteresis = typeof hysteresisRaw === "boolean" ? hysteresisRaw : false;
    if (outputMode === "standard" && !hysteresis)
        return null;
    const outputLowVoltage = getNumericParam(component, "outputLowVoltage", 0.4);
    const vThresholdPlus = getNumericParam(component, "vThresholdPlus", 0.05);
    const vThresholdMinus = getNumericParam(component, "vThresholdMinus", -0.05);
    if (vThresholdPlus <= vThresholdMinus) {
        throw new StampParameterError(`opampDCStamp: component "${component.id}" has vThresholdPlus (${String(vThresholdPlus)}) ≤ vThresholdMinus (${String(vThresholdMinus)}); Schmitt hysteresis requires a positive band`, {
            meta: {
                stampType: "opamp",
                parameter: "vThresholdPlus/vThresholdMinus",
                received: { vThresholdPlus, vThresholdMinus },
                expected: "vThresholdPlus > vThresholdMinus",
                componentId: component.id,
            },
        });
    }
    return {
        outputMode,
        hysteresis,
        outputLowVoltage,
        vThresholdPlus,
        vThresholdMinus,
    };
}
/**
 * Sprint 54.2a — Schmitt comparator branch. State-locked output
 * derived from previous Newton iterate, with hysteresis thresholds
 * on V_diff. Constant Norton at the output, no input coupling
 * (input admittance is intentionally zero — comparator inputs are
 * ideal high-Z). Open-collector flag controls the LOW-rail value
 * and may release the HIGH state to an external pull-up.
 */
function stampComparatorSchmitt(ctx, comp, O, vP, vN, Vsat) {
    const vDiff = vP - vN;
    const vOutPrev = nodeVoltage(ctx.xPrev, O);
    // High-state estimate: Vsat for push-pull, open-collector lets
    // pull-up float the line so any positive voltage counts as HIGH.
    const vHighRef = comp.outputMode === "open-collector" ? comp.outputLowVoltage + 0.5 : Vsat;
    const vLowRef = comp.outputMode === "open-collector" ? comp.outputLowVoltage : -Vsat;
    const midpoint = (vHighRef + vLowRef) / 2;
    const wasHigh = vOutPrev > midpoint;
    // State-dependent threshold:
    //   HIGH → flip LOW only when vDiff falls below vThresholdMinus
    //   LOW  → flip HIGH only when vDiff rises above vThresholdPlus
    const effectiveThreshold = wasHigh ? comp.vThresholdMinus : comp.vThresholdPlus;
    const goesHigh = wasHigh ? vDiff > effectiveThreshold : vDiff > effectiveThreshold;
    const vTarget = goesHigh ? vHighRef : vLowRef;
    const invRout = 1 / ROUT;
    if (comp.outputMode === "open-collector" && goesHigh) {
        // HIGH state in open-collector — release the line. Near-zero
        // conductance only; the external pull-up does the work.
        ctx.addY(O, O, OPEN_COLLECTOR_HIGH_G);
        return;
    }
    ctx.addY(O, O, invRout);
    if (O >= 0)
        ctx.rhs[O] = (ctx.rhs[O] ?? 0) + vTarget * invRout;
}
/**
 * Sprint 54.2a — open-collector comparator branch (no hysteresis).
 * Smooth tanh saturation drives the output target, but the output
 * stage is asymmetric: LOW state goes Norton-driven to
 * `outputLowVoltage`, HIGH state releases the line to a pull-up.
 *
 * The tanh linearisation cross-terms (g_in coupling from inputs)
 * are preserved in the LOW state so Newton-Raphson keeps its
 * smooth knee. In the HIGH state the output is high-Z so the
 * coupling collapses — no input admittance from the comparator.
 */
function stampComparatorOpenCollector(ctx, comp, P, N, O, vP, vN, A0, Vsat) {
    const vDiff = vP - vN;
    const arg = (A0 * vDiff) / Vsat;
    const tanhArg = Math.tanh(arg);
    if (tanhArg >= 0) {
        // HIGH (or zero-crossing) — release the output to the pull-up.
        ctx.addY(O, O, OPEN_COLLECTOR_HIGH_G);
        return;
    }
    // LOW state — Norton drive to V_OL with tanh-linearised input
    // coupling for Newton smoothness near the zero crossing.
    const vTarget = comp.outputLowVoltage;
    const sech2 = 1 - tanhArg * tanhArg;
    // Scale the input coupling by |tanhArg| so it vanishes when the
    // stamp re-enters the HIGH region (tanhArg → 0). Without this the
    // output row would carry residual A0-scaled coupling across the
    // discrete branch flip — destroys Newton convergence near the
    // transition.
    const couplingScale = -tanhArg; // > 0 in the LOW branch
    const gIn = A0 * sech2 * couplingScale;
    const invRout = 1 / ROUT;
    ctx.addY(O, O, invRout);
    ctx.addY(O, P, -gIn * invRout);
    ctx.addY(O, N, gIn * invRout);
    const IeqOut = (vTarget - gIn * vP + gIn * vN) * invRout;
    if (O >= 0)
        ctx.rhs[O] = (ctx.rhs[O] ?? 0) + IeqOut;
}
export const opampDCStamp = (ctx, component) => {
    const A0 = getNumericParam(component, "openLoopGain", 1e5);
    const Vsat = getNumericParam(component, "saturationVoltage", 12);
    const opampCtx = { stampType: "opamp", componentId: component.id };
    if (!(A0 > 0) || !Number.isFinite(A0)) {
        throw new StampParameterError(`opampDCStamp: component "${component.id}" has non-positive openLoopGain (got ${String(A0)})`, {
            meta: { ...opampCtx, parameter: "openLoopGain", received: A0, expected: "> 0 and finite" },
        });
    }
    if (!(Vsat > 0) || !Number.isFinite(Vsat)) {
        throw new StampParameterError(`opampDCStamp: component "${component.id}" has non-positive saturationVoltage (got ${String(Vsat)})`, {
            meta: {
                ...opampCtx,
                parameter: "saturationVoltage",
                received: Vsat,
                expected: "> 0 and finite",
            },
        });
    }
    const P = ctx.nodeIndex(getTerminalNetId(component, "in_pos"));
    const N = ctx.nodeIndex(getTerminalNetId(component, "in_neg"));
    const O = ctx.nodeIndex(getTerminalNetId(component, "out"));
    const vP = nodeVoltage(ctx.xPrev, P);
    const vN = nodeVoltage(ctx.xPrev, N);
    // Sprint 54.2a — comparator opt-in branches. Returns null for
    // legacy callers; every Family 1-3 op-amp falls straight through
    // to the Sprint 7 tanh path below, bit-identical.
    const comp = readComparatorParams(component);
    if (comp !== null) {
        if (comp.hysteresis) {
            stampComparatorSchmitt(ctx, comp, O, vP, vN, Vsat);
            return;
        }
        if (comp.outputMode === "open-collector") {
            stampComparatorOpenCollector(ctx, comp, P, N, O, vP, vN, A0, Vsat);
            return;
        }
    }
    const vDiff = vP - vN;
    // Smooth saturation. `tanh` is numerically stable — Math.tanh
    // already clamps to [-1, 1] with full precision, no need for a
    // hand-rolled guard.
    const arg = (A0 * vDiff) / Vsat;
    const tanhArg = Math.tanh(arg);
    const Vtarget = Vsat * tanhArg;
    // Derivative of Vtarget wrt vDiff:
    //   d/dx tanh(x) = sech²(x) = 1 − tanh²(x)
    // giving dVtarget/dvDiff = A0 · (1 − tanh²(arg)).
    const sech2 = 1 - tanhArg * tanhArg;
    const gIn = A0 * sech2;
    const invRout = 1 / ROUT;
    // Output Norton-style stamp: output node sees 1/Rout to "virtual
    // ground at V_target".
    ctx.addY(O, O, invRout);
    ctx.addY(O, P, -gIn * invRout);
    ctx.addY(O, N, gIn * invRout);
    // RHS: V_target / Rout minus the linearisation of the input
    // coupling at the operating point (P, N). Note gIn flips sign on
    // the in_neg contribution — dVtarget/dV_N = −gIn.
    const IeqOut = (Vtarget - gIn * vP + gIn * vN) * invRout;
    if (O >= 0)
        ctx.rhs[O] = (ctx.rhs[O] ?? 0) + IeqOut;
};
// Sprint 54.3h engine ext #10: opamp.out is volatile for
// open-collector / Schmitt comparator modes. The tanh-saturated
// transition produces large predictor-vs-corrector deltas at the
// comparator crossover (V_OL ↔ HIGH state) that the adaptive-step
// controller's LTE check would otherwise reject. Declaring "out" as
// a volatile terminal lets the controller exclude it from the LTE
// norm, accepting the step on the basis of the smooth-evolving
// nodes (input pins, rails, capacitor states).
//
// This is set unconditionally — Family 1-3 (standard rail-to-rail
// opamp) values also evolve smoothly through tanh, so excluding
// "out" is conservative: the LTE check is slightly looser there
// but the linear feedback nets that determine closed-loop behavior
// still anchor the acceptance decision.
export const opampDCStampDefinition = {
    type: "opamp",
    augments: [],
    applyDC: opampDCStamp,
    volatileTerminals: ["out"],
};
//# sourceMappingURL=opamp.js.map